// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'High-End Outdoor Living Reno in Holmby Hills, Los Angeles CA 90024',
  description:
    'A high-end outdoor living renovation in Holmby Hills featuring custom hardscape, pergola, landscaping, and luxury entertainment spaces by Modern Build.',
  link: '/services/outdoor-living',
  height: 'h-[467px]',
  image:
    '/projects/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug =
    'high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function HighEndOutdoorLivingHolmbyHills({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />

        <meta
          name="keywords"
          content="outdoor living Holmby Hills, luxury backyard remodel 90024, pergola renovation Los Angeles, outdoor kitchen remodel, Modern Build"
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'High-End Outdoor Living Renovation in Holmby Hills CA 90024',
                  item:
                    'https://modernbuildremodel.com/projects/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'High-End Outdoor Living Renovation in Holmby Hills, CA 90024 | Modern Build',
                description:
                  'Explore a luxury outdoor living renovation in Holmby Hills featuring custom pergolas, hardscape, landscaping, and entertainment spaces.',
                slug:
                  '/projects/high-end-outdoor-living-renovation-in-holmby-hills-los-angeles-90024',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="highEndOutdoorLivingHolmbyHills90024"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="High-End Outdoor Living Renovation in Holmby Hills"
        description="This luxury outdoor living renovation in Holmby Hills transformed a traditional backyard into a private resort-style retreat designed for entertaining and relaxation."
        extraText="Every feature was carefully planned to create seamless indoor-outdoor flow and long term durability."
        buttonText="Plan My Outdoor Project"
        buttonLink="/services/outdoor-living"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/NxWDE8YhTNI"
            serviceType="Outdoor Living Renovation"
            location="Holmby Hills, Los Angeles"
            description="Watch how this Holmby Hills backyard was transformed into a high-end outdoor living and entertainment space."
            serviceLink="/services/outdoor-living"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

      <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      High-End Outdoor Living Renovation in Holmby Hills, CA 90024
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This high-end outdoor living renovation in Holmby Hills transformed a traditional backyard into a luxurious, functional extension of the home. Every detail was planned to support entertaining, relaxation, and everyday enjoyment.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The homeowners desired a seamless combination of style, comfort, and practicality. Our team integrated architectural features with natural materials and premium finishes. For related home upgrades, see our <a
        href="https://modernbuildremodel.com/outdoor-living/california/los-angeles/90024"
        className="text-primary underline"
      >
        Outdoor Living
      </a> and <a
        href="https://modernbuildremodel.com/artificial-turf/california/los-angeles/90024"
        className="text-primary underline"
      >
        Artificial Turf
      </a> services.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original backyard lacked distinct zones, shade, and cohesive design. While spacious, it was not optimized for year-round use or outdoor entertaining.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our objective was to create a unified outdoor environment that balances comfort, aesthetics, and long-term durability.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Custom pergolas, premium hardscape, integrated seating, landscape lighting, and entertainment features were incorporated to create flexible spaces for different activities and seasons. For homeowners interested in adding water features, we also design and build luxury pools and spas similar to our <a
        href="https://modernbuildremodel.com/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326"
        className="text-primary underline"
      >
        pool and spa project in Porter Ranch
      </a>, combining resort-style design with long-term performance.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Natural textures and neutral tones were chosen to complement the home’s architecture and surrounding landscape.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Every phase, from foundation work to finishing touches, was completed with precision. Drainage, structural integrity, and material performance were prioritized for long-term reliability.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The finished space functions as a luxurious outdoor living room, ideal for hosting guests, family gatherings, or enjoying quiet relaxation.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      This renovation added significant lifestyle value and long-term property appeal.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Serving Holmby Hills and Greater Los Angeles
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Modern Build proudly serves Holmby Hills and surrounding Los Angeles communities with premium outdoor living solutions. Whether you’re planning a full backyard transformation or targeted upgrades, our team delivers exceptional results tailored to your needs.
    </p>

  </div>
</section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
